import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  CircularProgress,
  DialogContentText,
} from '@mui/material';
import PersonIcon from '@mui/icons-material/Person';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useCurrentOrgFprint, useEmulateUser } from '~/src/entities/user';
import { useLayoutContext } from '~/src/contexts';
import internalToolsService, {
  RemoteAccessRequest,
} from '~/src/services/api/internalToolsService';
import { EmptyRemoteAccessState } from '~/src/components/RemoteAccessDialog/EmptyRemoteAccessState';

type RemoteAccessDialogProps = {
  open?: boolean;
  onClose: () => void;
};

export const RemoteAccessDialog = ({
  open = false,
  onClose,
}: RemoteAccessDialogProps) => {
  const orgFprint = useCurrentOrgFprint();
  const { showToast } = useLayoutContext();
  const { mutate, isLoading, reset } = useEmulateUser();
  const [targets, setTargets] = useState<RemoteAccessRequest[]>([]);
  const [selectedTarget, setSelectedTarget] =
    useState<RemoteAccessRequest | null>(null);
  const [loading, setLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const hasFetched = useRef(false);

  const fetchTargets = useCallback(async () => {
    setLoading(true);
    setHasError(false);
    try {
      const response =
        await internalToolsService().getValidRemoteAccessRequests();
      setTargets(response.targets);
    } catch (error) {
      setHasError(true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!open || hasFetched.current) return;
    hasFetched.current = true;
    fetchTargets();
  }, [open, fetchTargets]);

  const onAfterClose = () => {
    reset();
    setSelectedTarget(null);
  };

  const handleTargetSelect = (target: RemoteAccessRequest) => {
    setSelectedTarget(target);
  };

  const handleSubmit = () => {
    if (!selectedTarget || isLoading) return;
    mutate(
      { orgFprint, email: selectedTarget.target_email },
      {
        onError: async (err) => {
          const message = await err.response.json();
          showToast('ERROR', {
            message: message ? message.message : err.message,
          });
        },
      },
    );
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: onAfterClose }}
      onClose={(_, reason) => {
        if (reason === 'escapeKeyDown') {
          onClose();
        }
      }}
    >
      <DialogTitle>Remote Access Requests</DialogTitle>
      <DialogContent>
        {hasError ? (
          <DialogContentText>
            Failed to load remote access requests. Please try again later.
          </DialogContentText>
        ) : (
          <>
            <DialogContentText sx={{ mb: 2 }}>
              Select a user to remote access:
            </DialogContentText>
            {loading ? (
              <div className="flex justify-center py-8">
                <CircularProgress />
              </div>
            ) : targets.length === 0 ? (
              <EmptyRemoteAccessState />
            ) : (
              <List
                sx={{
                  width: '100%',
                  bgcolor: 'background.paper',
                  maxHeight: 320,
                  overflowY: 'auto',
                  border: '1px solid #e0e0e0',
                  borderRadius: 1,
                  p: 0,
                }}
              >
                {targets.map((target) => {
                  const expiresSoon =
                    new Date(target.expires_at).getTime() - Date.now() <
                    24 * 60 * 60 * 1000;
                  return (
                    <ListItem key={target.target_id} disablePadding>
                      <ListItemButton
                        selected={
                          selectedTarget?.target_id === target.target_id
                        }
                        onClick={() => handleTargetSelect(target)}
                        sx={{
                          border:
                            selectedTarget?.target_id === target.target_id
                              ? '2px solid #1976d2'
                              : undefined,
                          borderRadius: 1,
                          bgcolor:
                            selectedTarget?.target_id === target.target_id
                              ? 'action.selected'
                              : undefined,
                        }}
                      >
                        <PersonIcon sx={{ mr: 2, color: 'primary.main' }} />
                        <ListItemText
                          primary={
                            <span className="font-medium">
                              {target.target_email}
                            </span>
                          }
                          secondary={
                            <span className={expiresSoon ? 'text-red-600' : ''}>
                              Expires:{' '}
                              {new Date(target.expires_at).toLocaleString()}
                              {expiresSoon && ' (expiring soon)'}
                            </span>
                          }
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          onClick={fetchTargets}
          disabled={loading}
          startIcon={<RefreshIcon />}
          aria-label="Refresh remote access targets"
        >
          Refresh
        </Button>
        <Button onClick={onClose} color="primary">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isLoading || !selectedTarget}
        >
          Confirm
        </Button>
      </DialogActions>
    </Dialog>
  );
};
